import {
  Avatar,
  Box,
  Button,
  Grid,
  Stack,
  Tab,
  Tabs,
} from '@ahamove/web-components';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { SyntheticEvent } from 'react';
import { useState, useEffect } from 'react';
import { useGetUserProfile } from '@/api/auth/useGetUserProfile';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { useGetAvailableCities } from '@/api/service/useGetAvailableCities';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import { detectPlatform } from '@/lib/common';
import { DEFAULT_DATE_TIME_FORMAT, EV_TRACK_KEY } from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import { homeConfig } from '@/modules/home/homeConfig';
import useIsMobile from '@/shared-hooks/useIsMobile';
import DateTimePicker from './dates/DateTimePicker';
import Location from './location/Location';
import { useNewBooking } from './NewBookingProvider';

export default function BookingBox() {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const { query } = useRouter();
  const isMobile = useIsMobile();
  const { resetDateTimeStore, expectedEndTime, startTime } = useDateTimeStore();
  const [tooltipError, setTooltipError] = useState(false);
  const [selectedTab, setSelectedTabs] = useState(0);
  const handleTooltipErrorClose = () => setTooltipError(false);
  const handleTooltipErrorOpen = () => setTooltipError(true);

  const { activeStep, nextStep, loading, errors, resetStep } = useNewBooking();
  const { rentType, setRentType } = useRentTypeStore();

  const vehicleStep = activeStep === 1;

  const { address, city, resetLocationStore } = useLocationStore();

  const listTabs = [
    {
      id: 0,
      label: t('home:e_bike'),
      image: '/static/images/Bike.png',
      type: 'BIKE',
    },
    {
      id: 1,
      label: t('home:e_car'),
      image: '/static/images/Car.png',
      type: 'CAR',
    },
  ];

  useEffect(() => {
    if (rentType === 'BIKE') {
      setSelectedTabs(0);
    } else {
      setSelectedTabs(1);
    }
  }, [rentType]);

  useEffect(() => {
    const { rentType } = query;
    if (rentType) {
      setRentType(rentType as string);
    }
  }, []);
  const { data: user } = useGetUserProfile();

  const handleNextStep = () => {
    if (Object.values(errors).length > 0) handleTooltipErrorOpen();
    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_find_vehicle_button, {
      user_id: user?._id || null,
      userId: user?._id || null,
      user_agent,
      city_id: city,
      location: address?.address,
      start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
      end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
      hour_renting: (expectedEndTime - startTime) / 3600,
      platform,
    });
    nextStep();
  };

  const handleChangeTab = (
    event: SyntheticEvent<Element, Event>,
    value: number
  ) => {
    let type = 'BIKE';
    if (value === 1) {
      type = 'CAR';
    }
    resetDateTimeStore();
    resetStep();
    setRentType(type);
  };

  const { data: availableCities } = useGetAvailableCities({
    service_type: rentType,
  });

  useEffect(() => {
    if (
      availableCities &&
      city &&
      !availableCities.filter((item: AvailableCity) => item?._id === city)
        .length
    ) {
      resetLocationStore();
    }
  }, [rentType]);

  return (
    <Stack
      direction="column"
      borderRadius={6}
      sx={{
        background:
          'linear-gradient(83.59deg, #FCFCFD 36.52%, rgba(252, 252, 253, 0.83) 98.8%)',
      }}
    >
      <Box
        data-test-id="serviceTypeTabsHeader"
        sx={{
          width: '100%',
          padding: isMobile ? '12px 16px 16px' : '12px 32px 0px',
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tabs
            variant={isMobile ? 'fullWidth' : 'standard'}
            onChange={handleChangeTab}
            value={selectedTab}
          >
            {listTabs.map((item, index) => (
              <Tab
                disableRipple
                sx={{
                  padding: '12px 24px !important',
                  whiteSpace: 'nowrap',
                }}
                key={item?.id}
                index={index}
                label={
                  <div
                    className={cn(
                      'text-body16 align-center flex flex-row items-center justify-center font-bold',
                      {
                        'text-primary-50': selectedTab === index,
                      }
                    )}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        marginRight: '8px',
                        width: '46px',
                        height: '32px',
                        '.MuiAvatar-img': {
                          objectFit: 'contain',
                        },
                      }}
                      src={item?.image}
                    />
                    {item?.label}
                  </div>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <Grid
        data-test-id="bookingBoxContainer"
        container
        direction={isMobile ? 'column' : 'row'}
        flexWrap="nowrap"
        gap={vehicleStep ? 0 : 3}
        px={isMobile ? 0 : 4}
        py={isMobile ? 2 : 3}
        mb={isMobile ? -16 : 0}
        position="relative"
        borderRadius={6}
        sx={{
          background:
            'linear-gradient(83.59deg, #FCFCFD 36.52%, rgba(252, 252, 253, 0.83) 98.8%)',
          boxShadow: '0px 40px 64px -32px rgba(15, 15, 15, 0.1)',
        }}
      >
        <Grid
          data-test-id="bookingBoxLocation"
          item
          xs={12}
          md={vehicleStep ? 6 : 4.75}
        >
          <Location
            tooltipError={tooltipError}
            handleCloseTooltip={handleTooltipErrorClose}
          />
        </Grid>

        <Grid
          data-test-id="bookingBoxRentTime"
          item
          xs={12}
          md={vehicleStep ? 6 : 4.25}
        >
          <DateTimePicker handleShowTooltip={handleTooltipErrorOpen} />
        </Grid>

        {activeStep === 0 ? (
          <Grid
            item
            xs={12}
            md={2.5}
            alignSelf="center"
            width="100%"
            px={isMobile ? 2 : 0}
          >
            <Button
              rounded
              fullWidth
              loading={loading || undefined}
              sx={{ fontSize: '20px', minHeight: 56, px: 2, py: '12px' }}
              onClick={() => handleNextStep()}
              data-test-id="searchVerhicleBtn"
            >
              {t('booking:booking_box.search')}
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Stack>
  );
}
